import IndexLevels from 'pages/Embedded/IndexLevels';
import EmbeddedDemo from 'pages/Embedded/demo';
import { lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { pageAction, pageName } from 'store/Permission/store';

const About = lazy(() => import('pages/About'));
const API = lazy(() => import('pages/API'));
const AdminApiKeys = lazy(() => import('pages/Admin/ApiKeys'));
const AdminBlogPost = lazy(() => import('pages/Admin/BlogPost'));
const AdminPressRelease = lazy(() => import('pages/Admin/PressRelease'));
const AdminLandingPages = lazy(() => import('pages/Admin/LandingPages'));
const AdminNews = lazy(() => import('pages/Admin/News'));
const AdminTeam = lazy(() => import('pages/Admin/Team'));
const AdminConsultations = lazy(() => import('pages/Admin/Consultations'));
const APIRaw = lazy(() => import('pages/APIRaw'));
const Login = lazy(() => import('pages/Auth/Login'));
const PasswordLogin = lazy(() => import('pages/Auth/Login/passwordLogin'));
const Logout = lazy(() => import('pages/Auth/Logout'));
const PasswordReset = lazy(() => import('pages/Auth/PasswordReset'));
const PasswordChange = lazy(() => import('pages/Auth/PasswordChange'));
const Profile = lazy(() => import('pages/Auth/Profile'));
const Register = lazy(() => import('pages/Auth/Register'));
const Verify = lazy(() => import('pages/Auth/Verify'));
const BlogPost = lazy(() => import('pages/Blog/post'));
const PressRelease = lazy(() => import('pages/PressRelease/post'));
const Qubits = lazy(() => import('pages/Qubits'));
const Contact = lazy(() => import('pages/Contact'));
const Home = lazy(() => import('pages/Home'));
const IndexPage = lazy(() => import('pages/IndexPage'));
const IndexBenchmark = lazy(() => import('pages/IndexBenchmark'));
const IndexWizard = lazy(() => import('pages/IndexWizard'));
const Indices = lazy(() => import('pages/Indices'));
const Garage = lazy(() => import('pages/Garage'));
const GaragePreview = lazy(() => import('pages/Garage/Preview'));
const NotFound = lazy(() => import('pages/NotFound'));
const Docs = lazy(() => import('pages/PDF'));
const Files = lazy(() => import('pages/File'));
const PrivacyPolicy = lazy(() => import('pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('pages/Terms'));
const Governance = lazy(() => import('pages/Governance'));
const SupplementalIndices = lazy(() => import('pages/Governance/SupplementalIndices'));
const SupportDocs = lazy(() => import('pages/SupportDocs'));
const adminPerms = lazy(() => import('pages/Admin/Permissions'));
const authHandler = lazy(() => import('pages/Auth/AuthHandler'));
const events = lazy(() => import('pages/Events'));
const eventAlbum = lazy(() => import('pages/Events/Album'));
const eventInvites = lazy(() => import('pages/Events/List'));
const FAQs = lazy(() => import('pages/FAQs'));

const APIOrNotFound = () => {
  const location = useLocation();
  const isApi = !!location.pathname.match(
    /(api.merqube.com|(\/api(\/staging|\/internal|\/main|\/indexapi|\/data|\/diss|\/indexapi-staging)))/
  );
  return isApi ? <APIRaw /> : <NotFound />;
};

const GoToStaging = () => {
  window.location.replace('https://staging.merqube.com');
  return <></>;
};

export const routes = {
  about: { path: '/about/our-story', component: About },
  aboutCareers: { path: '/about/careers', component: About },
  aboutTeam: { path: '/about/our-team', component: About },
  api: { path: '/api', component: API },
  contact: { path: '/contact', component: Contact },
  docs: { path: '/docs', component: Docs },
  verify: { path: '/verify', component: Verify },
  filesDirectoryPathname: { path: 'files/:directory/:pathname', component: Files },
  indexFilePathname: { path: 'files/:uuid/:label/:filename', component: Files },
  filesPathname: { path: 'files/:pathname', component: Files },
  governance: { path: '/governance', component: Governance },
  announcements: { path: '/governance/announcements', component: Governance },
  announcementFile: { path: '/governance/announcements/:filename', component: Files },
  consultations: { path: '/governance/consultations', component: Governance },
  consultationFile: { path: '/governance/consultations/:filename', component: Files },
  policies: { path: '/governance/policies', component: Governance },
  policieFile: { path: '/governance/policies/:filename', component: Files },
  supplementalData: { path: '/governance/supplemental-data', component: Governance },
  supplementalDataIndices: {
    path: '/governance/supplemental-data/indices/:language?',
    component: SupplementalIndices,
  },
  indexBenchmark: { path: '/indices/:indexName/benchmark', component: IndexBenchmark },
  indexBenchmarkRisk: { path: '/indices/:indexName/benchmark/risk', component: IndexBenchmark },
  indexBenchmarkMeasure: {
    path: '/indices/:indexName/benchmark/measure',
    component: IndexBenchmark,
  },
  indexBenchmarkStats: {
    path: '/indices/:indexName/benchmark/statistics',
    component: IndexBenchmark,
  },
  index: { path: '/index/:indexName/:language?', component: IndexPage },
  indexPage: { path: '/indices/:indexName/:language?', component: IndexPage },
  indices: { path: '/indices', component: Indices },
  customLanding: { path: '/indices/partners/:slug', component: Indices },
  garagePreview: { path: '/garage/preview', component: GaragePreview },
  news: { path: '/news/coverage', component: Qubits },
  blog: { path: '/news/blog', component: Qubits },
  blogdoc: { path: '/news/blog/file/:filename', component: Files },
  blogAttach: { path: '/news/blog/docs/:filename', component: Files },
  blogpost: { path: '/news/blog/:slug', component: BlogPost },
  press: { path: '/news/press', component: Qubits },
  newsPressFile: { path: '/news/press/:filename', component: Files },
  pressRelease: { path: '/news/press-release/:slug', component: PressRelease },
  faqs: { path: '/faqs', component: FAQs },
  passwordReset: { path: '/password-reset', component: PasswordReset },
  privacyPolicy: { path: '/privacy-notice', component: PrivacyPolicy },
  rawApiUrl: { path: '/raw-api/:url', component: APIRaw },
  staging: { path: '/staging', component: GoToStaging },
  supportDocs: { path: '/support/documents', component: SupportDocs },
  supportDocsItems: { path: '/support/documents/:filename', component: Files },
  swagger: { path: '/swagger', component: API },
  termsOfUse: { path: '/terms-of-use', component: TermsOfUse },
  logout: { path: '/logout', component: Logout },
  authHandler: { path: '/auth-handler', component: authHandler },
  events: { path: '/events/:eventName', component: events },
  eventAlbum: { path: '/events/:eventName/album', component: eventAlbum },
  embeddedIndexLevels: { path: '/embedded/index/:indexName/:language?', component: IndexLevels },
  embeddedDemo: { path: '/embedded/demo', component: EmbeddedDemo },

  // default routes, must be at the bottom
  main: {
    path: '/',
    component: Home,
  },
  notFound: {
    path: '*',
    component: APIOrNotFound,
  },
};

interface AuthRoute {
  path: string;
  component: any;
  level: 'guest' | 'user' | 'verified-user';
}
interface AuthRoutes {
  login: AuthRoute;
  passwordLogin: AuthRoute;
  register: AuthRoute;
  protectedFiles: AuthRoute;
  profile: AuthRoute;
  accountGarage: AuthRoute;
  passwordChange: AuthRoute;
  accountApiKeys: AuthRoute;
  garageMyIndices: AuthRoute;
  garageMyInQubator: AuthRoute;
  garageCreateIndex: AuthRoute;
  garageEAP: AuthRoute;
  garageEAPDataBrowser: AuthRoute;
}

export const authRoutes: AuthRoutes = {
  login: { path: '/login', component: Login, level: 'guest' },
  passwordLogin: { path: '/login/legacy', component: PasswordLogin, level: 'guest' },
  register: { path: '/register', component: Register, level: 'guest' },
  protectedFiles: { path: 'protected/:pathname', component: Files, level: 'verified-user' },
  profile: { path: '/account/profile', component: Profile, level: 'user' },
  accountGarage: { path: '/garage', component: Garage, level: 'verified-user' },
  accountApiKeys: { path: '/account/api-keys', component: AdminApiKeys, level: 'verified-user' },
  passwordChange: { path: '/account/password-change', component: PasswordChange, level: 'user' },
  garageMyIndices: { path: '/garage/my-indices', component: Garage, level: 'verified-user' },
  garageMyInQubator: { path: '/garage/the-inqubator', component: Garage, level: 'verified-user' },
  garageCreateIndex: { path: '/garage/new-index', component: Garage, level: 'verified-user' },
  garageEAP: {
    path: '/garage/enterprise-access-point',
    component: Garage,
    level: 'verified-user',
  },
  garageEAPDataBrowser: {
    path: '/garage/data-browser',
    component: Garage,
    level: 'verified-user',
  },
};

interface AdminRoute {
  path: string;
  component: any;
  namespace: pageName;
  level?: pageAction;
}

interface AdminRoutes {
  accountNewIndex: AdminRoute;
  accountTemplateIndex: AdminRoute;
  adminLandingPages: AdminRoute;
  adminNews: AdminRoute;
  adminPress: AdminRoute;
  adminBlog: AdminRoute;
  adminBlogPosts: AdminRoute;
  adminBlogDocs: AdminRoute;
  adminTeam: AdminRoute;
  adminNewBlogPost: AdminRoute;
  adminEditBlogPost: AdminRoute;
  adminPerms: AdminRoute;
  eventInvites: AdminRoute;
  adminNewPressRelease: AdminRoute;
  adminEditPressRelease: AdminRoute;
  adminUpdatePressRelease: AdminRoute;
  adminConsultations: AdminRoute;
}

export const adminRoutes: AdminRoutes = {
  accountNewIndex: {
    path: `/garage/new-index/:indexType`,
    namespace: 'userIndicesDashboard',
    level: 'VIEW',
    component: () => <IndexWizard />,
  },
  accountTemplateIndex: {
    path: `/garage/new-index/:indexType/:templateId`,
    namespace: 'userIndicesDashboard',
    level: 'VIEW',
    component: () => <IndexWizard />,
  },
  adminLandingPages: {
    path: `/admin/landing-pages`,
    namespace: 'adminNews',
    component: AdminLandingPages,
  },
  adminNews: {
    path: `/admin/news/coverage`,
    namespace: 'adminNews',
    component: AdminNews,
  },
  adminPress: {
    path: `/admin/news/press`,
    namespace: 'adminNews',
    component: AdminNews,
  },
  adminBlog: {
    path: `/admin/news/blog`,
    namespace: 'adminNews',
    component: AdminNews,
  },
  adminBlogPosts: {
    path: `/admin/news/blog-posts`,
    namespace: 'adminNews',
    component: AdminNews,
  },
  adminBlogDocs: {
    path: `/admin/news/blog-docs`,
    namespace: 'adminNews',
    component: AdminNews,
  },
  adminNewBlogPost: {
    path: `/admin/news/blog/post`,
    namespace: 'adminNews',
    component: AdminBlogPost,
  },
  adminEditBlogPost: {
    path: `/admin/news/blog/:slug`,
    namespace: 'adminNews',
    component: AdminBlogPost,
  },
  adminNewPressRelease: {
    path: `/admin/news/press-release/post`,
    namespace: 'adminNews',
    component: AdminPressRelease,
  },
  adminEditPressRelease: {
    path: `/admin/news/press-release/:slug`,
    namespace: 'adminNews',
    component: AdminPressRelease,
  },
  adminUpdatePressRelease: {
    path: `/admin/news/press-release-update/:id`,
    namespace: 'adminNews',
    component: AdminPressRelease,
  },
  adminTeam: {
    path: `/admin/team`,
    namespace: 'adminTeam',
    component: AdminTeam,
  },
  adminPerms: {
    path: `/admin/permissions`,
    namespace: 'adminNews',
    component: adminPerms,
  },
  eventInvites: {
    path: `/events/:eventName/list`,
    namespace: 'adminTeam',
    component: eventInvites,
  },
  adminConsultations: {
    path: `/admin/consultations`,
    namespace: 'adminNews',
    component: AdminConsultations,
  },
};

const allRoutes = { ...routes, ...authRoutes, ...adminRoutes };
type RouteConfig = {
  [K in keyof typeof allRoutes]: string;
};
const routeConfig = Object.keys(allRoutes).reduce<RouteConfig>(
  (acc, route) => ({
    ...acc,
    [route]: (allRoutes as any)[route].path,
  }),
  {} as RouteConfig
);

export default routeConfig;
